import React from 'react';
import { Formik } from 'formik';
import {
    TextField,
    withStyles,
    Button,
    Typography,
    FormHelperText,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
} from '@material-ui/core';
import * as Yup from 'yup';
import { hasPermission } from '../../sc-utilities';
import { updateKey } from '../TypicalProblem/TypicalProblemForm';

const styles = (theme) => ({
    root: {
        width: 'auto',
        height: 'auto',
        padding: theme.spacing(2),
        margin: theme.spacing(2),
    },
    submit: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        backgroundColor: theme.palette.primary.main,
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
        width: '100%',
        marginTop: '10px',
    },
    helperText: {
        marginTop: '4px',
    },
    formControl: {
        width: '100%',
    },
});

export const types = [
    { value: 'system', displayValue: 'System SKU pattern' },
    { value: 'custom', displayValue: 'Customer SKU pattern' },
];

const SKUPatternForm = (props) => {
    const {
        classes,
        formSettings,
        initialValues,
        handleSubmit,
        translations,
        permissions,
        CustomerIDPreviousState,
        fromEdit,
    } = props;

    const productTypes = [
        { value: 'PCProduct', label: translations['pc_product'] || 'PC Product' },
        { value: 'MobileProduct', label: translations['mobile_product'] || 'Mobile Product' },
        { value: 'StorageProduct', label: translations['storage_product'] || 'Storage Product' },
    ];

    return (
        <div className={classes.root}>
            <Formik
                initialValues={initialValues}
                onSubmit={async (values, { setSubmitting }) => {
                    setSubmitting(true);
                    await handleSubmit(values);
                    setSubmitting(false);
                }}
                validationSchema={Yup.object().shape(formSettings.Validation)}
            >
                {(formikProps) => {
                    const {
                        values,
                        touched,
                        errors,
                        isValid,
                        dirty,
                        isSubmitting,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        setFieldValue,
                    } = formikProps;

                    return (
                        <form onSubmit={handleSubmit}>
                            <Typography variant={'h6'}>
                                {formSettings.FormTitle ? formSettings.FormTitle : null}
                            </Typography>
                            <TextField
                                name="Name"
                                value={values.Name}
                                onChange={(e) => {
                                    handleChange(e);
                                    setFieldValue('Key', e.target.value);
                                }}
                                onBlur={handleBlur}
                                error={!!(errors.Name && touched.Name)}
                                placeholder={translations['name'] || 'Name'}
                                label={translations['name'] || 'Name'}
                                helperText={errors.Name}
                                margin={'dense'}
                                fullWidth={true}
                            />
                            <TextField
                                name="Pattern"
                                value={values.Pattern}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.Pattern && touched.Pattern)}
                                placeholder={translations['pattern'] || 'Pattern'}
                                label={translations['pattern'] || 'Pattern'}
                                helperText={errors.Pattern}
                                margin={'dense'}
                                fullWidth={true}
                            />
                            <FormControl className={classes.formControl} margin={'dense'}>
                                <InputLabel shrink>
                                    {translations['select_product_type'] || 'Please select product type'}
                                </InputLabel>
                                <Select
                                    name="ProductType"
                                    value={values.ProductType}
                                    onChange={handleChange}
                                    label={translations['product_type'] || 'Product type'}
                                    helperText={errors.ProductType}
                                    margin={'dense'}
                                    fullWidth
                                >
                                    {(productTypes ?? []).map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {values.ProductType === 'PCProduct' && (
                                <FormHelperText>
                                    {translations['available_placeholders'] || 'Available placeholders'}: Vendor, Model,
                                    Chassis, SerialNumber, LabelComment1, LabelComment2, LabelComment3, LabelComment4,
                                    Notes, Grade, OS, SecurazeID, ProductID, OrderID, ColliID, TransportColliID,
                                    TransportColliName, ExternalID, StorageStatus, TypicalProblems, SMARTOverall,
                                    SMARTHealth, StorageSize, StorageSerial, BatteryHealth, BatteryCapacity, MDM, DEP,
                                    RAM, Resolution, ScreenSize
                                </FormHelperText>
                            )}
                            {values.ProductType === 'MobileProduct' && (
                                <>
                                    <FormHelperText>
                                        {translations['available_placeholders'] || 'Available placeholders'}: Vendor,
                                        Model, ModelShortened, AdditionalModelInfo, SerialNumber, IMEI, IMEI2,
                                        LabelComment1, LabelComment2, LabelComment3, LabelComment4, Notes, Grade, OS,
                                        SecurazeID, ProductID, OrderID, ColliID, TransportColliID, TransportColliName,
                                        ExternalID, StorageStatus, TypicalProblems, StorageSize, StorageSerial,
                                        BatteryHealth, BatteryCapacity, InternalName, ANumber, FindMyIphone, Carrier,
                                        CarrierLock, Financed, MDM, SimLock, Stolen, OperatingSystemVersion,
                                        OperatingSystemVersionPreProcessing, Processor, DeviceColor, ECID, MEID,
                                        ProductVersion, NetworkStandard, AvailableSIMCardSlots, InstalledSIMCards,
                                        SDCardAmount, DisplaySize, IsRooted, PortName
                                    </FormHelperText>
                                </>
                            )}
                            {values.ProductType === 'StorageProduct' && (
                                <>
                                    <FormHelperText>
                                        {translations['available_placeholders'] || 'Available placeholders'}: Vendor,
                                        Model, Chassis, SerialNumber, LabelComment1, LabelComment2, LabelComment3,
                                        LabelComment4, Notes, Grade, OS, SecurazeID, ProductID, OrderID, ColliID,
                                        TransportColliID, TransportColliName, ExternalID, StorageStatus,
                                        TypicalProblems, SMARTOverall, SMARTHealth, StorageSize, StorageSerial,
                                        BatteryHealth, BatteryCapacity
                                    </FormHelperText>
                                </>
                            )}
                            <FormHelperText>
                                {`${
                                    translations['sku_pattern_helper_text'] ||
                                    'Placeholders should be marked like so: ${PLACEHOLDER}.'
                                } ${translations['example'] || 'Example'}: \${Vendor}.\${Model}.\${SerialNumber}`}
                            </FormHelperText>
                            {hasPermission(permissions, 'system_sku_pattern_create') ? (
                                <>
                                    <FormControl className={classes.formControl} margin={'dense'}>
                                        <InputLabel shrink>
                                            {translations['sku_pattern_type'] || 'Please select SKU pattern type'}
                                        </InputLabel>
                                        <Select
                                            name="CustomerID"
                                            label={translations['sku_pattern_type'] || 'Please select SKU pattern type'}
                                            margin={'dense'}
                                            value={values?.CustomerID?.toString() || ''}
                                            onChange={handleChange}
                                            disabled={
                                                values?.CustomerID?.toString() === 'system' &&
                                                fromEdit &&
                                                CustomerIDPreviousState === 'system'
                                            }
                                        >
                                            {(types ? [...types] : []).map((option) => (
                                                <MenuItem key={option.value} value={option.value.toString()}>
                                                    {option.displayValue}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {values?.CustomerID?.toString() === 'system' ? (
                                        <TextField
                                            name="Key"
                                            value={values?.Key ? updateKey(values?.Key) : ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!errors.Key}
                                            placeholder={translations['key'] || 'Key'}
                                            label={translations['key'] || 'Key'}
                                            helperText={errors.Key}
                                            margin={'dense'}
                                            fullWidth={true}
                                        />
                                    ) : null}
                                </>
                            ) : null}
                            <div>
                                {formSettings.ButtonSubmit ? (
                                    <Button
                                        className={classes.submit}
                                        variant="contained"
                                        color={formSettings.ButtonSubmit.Color}
                                        type="submit"
                                        style={{ color: isSubmitting || !(isValid && dirty) ? null : 'white' }}
                                        disabled={isSubmitting || !(isValid && dirty)}
                                    >
                                        {isSubmitting ? (
                                            <CircularProgress size={24} />
                                        ) : (
                                            formSettings.ButtonSubmit.Title
                                        )}
                                    </Button>
                                ) : null}
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default withStyles(styles, { withTheme: true })(SKUPatternForm);
